<script>
export default {
    name: 'AppSidebar',
};
</script>

<script setup>
import { ref, computed } from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { getConfig } from '@/config.ts';
import { useStore } from '@/store/index.js';
import i18n from '@/plugins/i18n.ts';
import SidebarIcon from '@/assets/img/logo/sidebar-icon.svg';
import SidebarLogo from '@/assets/img/logo/sidebar.svg';
import AppSidebarCopyright from '@/components/app/sidebar/copyright.vue';
import SidebarBackup from '@/components/app/sidebar/section/backup.vue';
import SidebarSettings from '@/components/app/sidebar/section/settings.vue';
import { HELP_AND_INFORMATION, POLICY_AND_AGREEMENTS, PRODUCTS } from '@/store/modules/acl-access/constants.ts';
import { forEachAppModule } from '@/modules/registry';
import { emitter } from '@/utils/event/emitter.ts';
import { APP } from '@/utils/event/constants.ts';
import { useAppSidebarStore } from '@/store/pinia/app/sidebar.js';

const store = useStore();

const sidebarStore = useAppSidebarStore();
const { collapsed } = storeToRefs(sidebarStore);

function toggleSidebar() {
    sidebarStore.toggle();
}

const breakpoints = useBreakpoints(breakpointsTailwind);

function sectionNavigate() {
    if (collapsed.value || breakpoints.greaterOrEqual('md')) return;

    toggleSidebar();
}

const showIcon = computed(() => {
    const order = breakpoints.current().value;

    if (order.indexOf(breakpoints.active().value) < order.indexOf('md')) return false;

    return collapsed.value;
});

const { value: coBrandingDomain } = getConfig()?.cobranding ?? {};

function getImagePath(name, def) {
    return coBrandingDomain ? `${import.meta.env.BASE_URL}img/cobranding/${coBrandingDomain}/${name}` : def;
}

const logo = computed(() => getImagePath('sidebar.svg', SidebarLogo));
const icon = computed(() => getImagePath('sidebar-icon.svg', SidebarIcon));

const isBackupAvailable = computed(() => store.getters['aclAccess/isGranted'](PRODUCTS.BACKUP));

function getInjectedModulesSidebars(key) {
    const components = [];

    forEachAppModule((module, name) => {
        const sidebarValue = module.sidebar?.[key];

        if (!sidebarValue) return;

        if (typeof sidebarValue !== 'object') {
            components.push({
                name,
                component: sidebarValue,
            });

            return;
        }

        Object.keys(sidebarValue).forEach((sidebarName) => {
            if (store.getters['aclAccess/isGranted'](sidebarName) === false) return;

            components.push({
                name: sidebarName,
                component: sidebarValue[sidebarName],
            });
        });
    });

    return components;
}

const mainSections = ref([]);
const additionalSections = ref([]);

function initSidebarSections() {
    mainSections.value = getInjectedModulesSidebars('main');
    additionalSections.value = getInjectedModulesSidebars('additional');
}

initSidebarSections();

emitter.on(APP.MODULES, initSidebarSections);

const helpButtons = computed(() => [
    {
        acl: HELP_AND_INFORMATION,
        icon: {
            name: 'question-circle',
            prefix: 'far',
        },
        label: i18n.t('header breadcrumb help'),
        route: {
            name: 'help',
        },
    },
    {
        acl: POLICY_AND_AGREEMENTS,
        icon: {
            name: 'file-signature',
            prefix: 'fas',
        },
        label: i18n.t('header breadcrumb policy'),
        route: {
            name: 'policy',
        },
    },
].filter(({ acl }) => store.getters['aclAccess/isGranted'](acl)));
</script>

<template>
    <div>
        <div
            class="fixed zi-120 flex h-full shrink-0 bg-linear-to-b from-secondary-950 to-secondary-800 shadow-2xl transition-all duration-500 ease-in-out @3xl/app:relative @3xl/app:bg-none @3xl/app:shadow-none"
            :class="collapsed ? 'w-64 -translate-x-full @3xl/app:w-14 @3xl/app:translate-x-0' : 'w-64 translate-x-0'"
        >
            <div class="relative flex w-full flex-col">
                <div class="relative flex h-16 items-center justify-center">
                    <div
                        class="flex h-full items-center justify-center overflow-hidden transition-all duration-500 ease-in-out [direction:rtl]"
                        :class="collapsed ? '@3xl/app:w-10' : 'w-full'"
                    >
                        <div
                            class="flex items-center"
                            :data-test="dataTest('logo')"
                        >
                            <router-link
                                class="flex shrink-0 items-center justify-center"
                                to="/"
                            >
                                <div class="relative flex items-center justify-center">
                                    <template v-if="coBrandingDomain">
                                        <img
                                            v-if="showIcon"
                                            :src="icon"
                                            class="h-8"
                                            alt="MigrateMyCRM"
                                        >
                                        <img
                                            v-else
                                            :src="logo"
                                            class="h-10 w-52"
                                            alt="MigrateMyCRM"
                                        >
                                    </template>
                                    <template v-else>
                                        <img
                                            class="absolute hidden h-8 @3xl/app:block"
                                            :class="{
                                                'translate-x-[84px] opacity-100 transition-transform duration-500': showIcon,
                                                'translate-x-[38px] -translate-y-[10px] scale-[0.63] opacity-0 [transition:opacity_50ms_450ms_ease-in-out,translate_500ms_ease-in-out,scale_500ms_ease-in-out]': !showIcon,
                                            }"
                                            :src="icon"
                                            alt="MigrateMyCRM"
                                        >
                                        <img
                                            class="h-10 w-52 transition-opacity duration-300 ease-in-out"
                                            :class="showIcon ? '@3xl/app:opacity-0' : 'delay-300 @3xl/app:opacity-100'"
                                            :src="logo"
                                            alt="MigrateMyCRM"
                                        >
                                    </template>
                                </div>
                            </router-link>
                        </div>
                    </div>
                </div>
                <base-scroll
                    class="flex flex-1 flex-col"
                    wrap-class="flex flex-1 flex-col"
                    view-class="flex flex-1 flex-col justify-between px-2 pt-2 pb-1 text-gray-300 space-y-12"
                >
                    <div class="-mx-2 flex shrink-0 flex-col space-y-1">
                        <sidebar-backup
                            v-if="isBackupAvailable"
                            class="px-2"
                            @navigate="sectionNavigate"
                        />
                        <component
                            v-for="section in mainSections"
                            :key="section.name"
                            :is="section.component"
                            class="px-2"
                            @navigate="sectionNavigate"
                        />
                    </div>
                    <div class="flex shrink-0 flex-col gap-y-1">
                        <component
                            v-for="section in additionalSections"
                            :key="section.name"
                            :is="section.component"
                            @navigate="sectionNavigate"
                        />
                        <sidebar-settings
                            @navigate="sectionNavigate"
                        />
                        <div v-if="helpButtons.length" class="flex flex-col space-y-1 border-t border-white/20 pt-1">
                            <router-link
                                v-for="(helpButton, index) in helpButtons"
                                v-slot="{ href, navigate, isActive }"
                                :key="index"
                                :to="helpButton.route"
                                custom
                                @click.native="sectionNavigate"
                            >
                                <a
                                    :href="href"
                                    class="flex w-full rounded-md font-semibold tracking-wide uppercase hover:bg-black/30 hover:text-white focus:text-white focus:outline-hidden"
                                    :class="{
                                        'bg-black/40 text-white shadow-inner shadow-black/30': isActive,
                                        'text-gray-300': !isActive,
                                    }"
                                    :title="collapsed ? helpButton.label : undefined"
                                    @click="navigate"
                                >
                                    <div
                                        class="flex w-full items-center space-x-3 py-2 transition-spacing duration-500"
                                        :class="collapsed ? 'px-2' : 'px-4'"
                                    >
                                        <div class="flex h-5 w-6 shrink-0 items-center justify-center">
                                            <base-icon
                                                class="text-xl"
                                                :name="helpButton.icon.name"
                                                :prefix="helpButton.icon.prefix"
                                                fixed-width
                                            />
                                        </div>
                                        <span class="overflow-hidden text-xs leading-none whitespace-nowrap">
                                            {{ helpButton.label }}
                                        </span>
                                    </div>
                                </a>
                            </router-link>
                        </div>
                    </div>
                </base-scroll>

                <app-sidebar-copyright class="mx-2 border-t border-white/20 py-2" :collapsed="collapsed" />
            </div>
        </div>
        <div
            class="fixed zi-110 h-screen w-screen transition-all duration-300 ease-in-out @3xl/app:hidden"
            :class="collapsed ? 'pointer-events-none bg-gray-800/0' : 'bg-gray-800/50 backdrop-blur-sm'"
            @click="toggleSidebar"
        />
    </div>
</template>
